import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { inventoryService } from '../../services/inventoryService';
import type { InventorySample } from '../../types/inventory';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (sample: InventorySample) => void;
  itemId: string;
  readOnly?: boolean;
}

export const CreateSampleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onCreate,
  itemId,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setError('');
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (readOnly) {
      onClose();
      return;
    }
    if (!date) {
      setError(t('inventory.sampleModal.errors.dateRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const newSample = await inventoryService.createSample(itemId, date, notes);
      onCreate(newSample);
      onClose();
    } catch (err: unknown) {
      console.error(t('inventory.sampleModal.errors.createFailed'), err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError((err as any).response?.data?.error || t('inventory.sampleModal.errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('inventory.sampleModal.title')} className="max-w-md">
      <div className="space-y-4">
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div>
          <label className="text-sm font-medium">{t('reports.general.table.date')}</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            onClick={openNativeDatePickerSafely}
            onKeyDown={preventManualDateEntry}
            onPaste={preventManualDatePasteOrDrop}
            onDrop={preventManualDatePasteOrDrop}
            className="mt-1 w-full border rounded-md p-2"
            disabled={loading || readOnly}
          />
        </div>

        <div>
          <label className="text-sm font-medium">{t('reports.details.notes')}</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 w-full border rounded-md p-2"
            disabled={loading || readOnly}
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-md" disabled={loading}>
            {readOnly ? t('forms.actions.close') : t('auth.common.cancel')}
          </button>
          <button onClick={handleSave} className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50" disabled={loading || readOnly}>
            {loading ? t('forms.actions.saving') : t('forms.actions.save')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
